import { Extension } from "@tiptap/core"
import type { Node as ProseMirrorNode } from "@tiptap/pm/model"
import { Plugin, PluginKey } from "@tiptap/pm/state"
import { Decoration, DecorationSet } from "@tiptap/pm/view"

export type LensType = "sentence-difficulty" | "sentence-variation" | null

interface LensesOptions {
  locale: string
}

interface SentenceInfo {
  from: number
  to: number
  text: string
  wordCount: number
  letterCount: number
}

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    lenses: {
      setLens: (lens: LensType) => ReturnType
      clearLens: () => ReturnType
    }
  }
}

export const lensesKey = new PluginKey("lenses")
const LENSES_META_KEY = "lensesType"

interface PluginState {
  decorations: DecorationSet
  lensType: LensType
}

interface Segmenters {
  sentence: Intl.Segmenter
  word: Intl.Segmenter
}

const segmenterCache = new Map<string, Segmenters>()

function getSegmenters(locale: string): Segmenters {
  const cached = segmenterCache.get(locale)
  if (cached) return cached

  const segmenters: Segmenters = {
    sentence: new Intl.Segmenter(locale, { granularity: "sentence" }),
    word: new Intl.Segmenter(locale, { granularity: "word" }),
  }

  segmenterCache.set(locale, segmenters)
  return segmenters
}

function countWords(text: string, segmenters: Segmenters): number {
  return Array.from(segmenters.word.segment(text)).filter(
    segment => segment.isWordLike,
  ).length
}

function countLetters(text: string): number {
  return text.match(/\w/g)?.length ?? 0
}

interface ParagraphInfo {
  sentences: SentenceInfo[]
}

function extractSentencesFromNode(
  node: ProseMirrorNode,
  nodePos: number,
  segmenters: Segmenters,
): SentenceInfo[] {
  const textContent: { text: string; pos: number }[] = []

  node.descendants((child, pos) => {
    if (child.isText && child.text) {
      textContent.push({ text: child.text, pos: nodePos + pos + 1 })
    }
    return true
  })

  if (textContent.length === 0) return []

  let fullText = ""
  const posMap: number[] = []

  for (const { text, pos } of textContent) {
    for (let i = 0; i < text.length; i++) {
      posMap.push(pos + i)
      fullText += text[i]
    }
  }

  return Array.from(segmenters.sentence.segment(fullText)).flatMap(segment => {
    const rawText = segment.segment
    const sentenceText = rawText.trim()
    if (!sentenceText) return []

    const leadingWhitespace = rawText.length - rawText.trimStart().length
    const trailingWhitespace = rawText.length - rawText.trimEnd().length

    const startIdx = segment.index + leadingWhitespace
    const endIdx = segment.index + rawText.length - trailingWhitespace

    const from = posMap[startIdx]
    const to = posMap[endIdx - 1]

    if (from === undefined || to === undefined) {
      return []
    }

    return {
      from,
      to: to + 1,
      text: sentenceText,
      wordCount: countWords(sentenceText, segmenters),
      letterCount: countLetters(sentenceText),
    }
  })
}

function extractParagraphs(
  doc: ProseMirrorNode,
  segmenters: Segmenters,
): ParagraphInfo[] {
  const paragraphs: ParagraphInfo[] = []

  doc.descendants((node, pos) => {
    if (node.type.name === "paragraph") {
      const sentences = extractSentencesFromNode(node, pos, segmenters)
      if (sentences.length > 0) {
        paragraphs.push({ sentences })
      }
      return false
    }
    return true
  })

  return paragraphs
}

export type LensSeverity = "amber" | "orange"

function getReadingLevel(sentence: SentenceInfo): number {
  const { letterCount, wordCount } = sentence
  if (wordCount === 0) return 0

  // Hemingway's formula: 4.71 * (letters/words) + 0.5 * words - 21.43
  // From https://www.freecodecamp.org/news/https-medium-com-samwcoding-deconstructing-the-hemingway-app-8098e22d878d/
  const level = Math.round(
    (4.71 * letterCount) / wordCount + 0.5 * wordCount - 21.43,
  )
  return Math.max(0, level)
}

function getSentenceDifficultySeverity(
  sentence: SentenceInfo,
): LensSeverity | null {
  const { wordCount } = sentence

  const level = getReadingLevel(sentence)
  if (wordCount < 14) return null
  if (level >= 14) return "orange"
  if (level >= 10) return "amber"

  return null
}

const SIMILARITY_RATIO_THRESHOLD = 0.25 // 25% difference

function isSimilarLength(a: number, b: number): boolean {
  if (a === 0 || b === 0) return a === b
  const ratio = Math.abs(a - b) / Math.max(a, b)
  return ratio <= SIMILARITY_RATIO_THRESHOLD
}

function findStreakStart(sentences: SentenceInfo[], index: number): number {
  let streakStart = index

  for (let i = index - 1; i >= 0; i--) {
    const current = sentences[streakStart]!
    const prev = sentences[i]!
    if (isSimilarLength(current.wordCount, prev.wordCount)) {
      streakStart = i
    } else {
      break
    }
  }

  return streakStart
}

function getSentenceVariationSeverity(
  sentences: SentenceInfo[],
  index: number,
): LensSeverity | null {
  if (index === 0) return null

  const current = sentences[index]!
  const streakStart = findStreakStart(sentences, index - 1)
  const streakStartSentence = sentences[streakStart]!

  if (!isSimilarLength(current.wordCount, streakStartSentence.wordCount)) {
    return null
  }

  const streakLength = index - streakStart + 1
  if (streakLength === 2 || streakLength === 3) return "amber"

  return "orange"
}

function buildLensDecorations(
  doc: ProseMirrorNode,
  lensType: LensType,
  segmenters: Segmenters,
): DecorationSet {
  if (!lensType) return DecorationSet.empty

  const paragraphs = extractParagraphs(doc, segmenters)
  if (paragraphs.length === 0) return DecorationSet.empty

  const decorations = paragraphs.flatMap(paragraph => {
    return paragraph.sentences.flatMap((sentence, index) => {
      const severity =
        lensType === "sentence-difficulty"
          ? getSentenceDifficultySeverity(sentence)
          : getSentenceVariationSeverity(paragraph.sentences, index)
      if (!severity) return []

      return Decoration.inline(
        sentence.from,
        sentence.to,
        {
          class: `lens-decoration lens-${severity}`,
        },
        {
          lensType,
          severity,
          wordCount: sentence.wordCount,
        },
      )
    })
  })

  return DecorationSet.create(doc, decorations)
}

export const LensesExtension = Extension.create<LensesOptions>({
  name: "lenses",

  addOptions() {
    return {
      locale: "en",
    }
  },

  addCommands() {
    return {
      setLens:
        (lens: LensType) =>
        ({ tr, dispatch }) => {
          if (dispatch) {
            tr.setMeta(LENSES_META_KEY, lens)
            dispatch(tr)
          }
          return true
        },
      clearLens:
        () =>
        ({ tr, dispatch }) => {
          if (dispatch) {
            tr.setMeta(LENSES_META_KEY, null)
            dispatch(tr)
          }
          return true
        },
    }
  },

  addProseMirrorPlugins() {
    const segmenters = getSegmenters(this.options.locale)

    return [
      new Plugin({
        key: lensesKey,
        state: {
          init: (): PluginState => {
            return {
              decorations: DecorationSet.empty,
              lensType: null,
            }
          },
          apply: (tr, value: PluginState, _oldState, newState): PluginState => {
            const metaLens = tr.getMeta(LENSES_META_KEY)
            let lensType = value.lensType

            if (metaLens !== undefined) {
              lensType = metaLens
            }

            const shouldRebuild =
              metaLens !== undefined || (tr.docChanged && lensType !== null)

            if (shouldRebuild) {
              const decorations = buildLensDecorations(
                newState.doc,
                lensType,
                segmenters,
              )
              return { decorations, lensType }
            }

            if (tr.docChanged && value.decorations !== DecorationSet.empty) {
              return {
                decorations: value.decorations.map(tr.mapping, newState.doc),
                lensType,
              }
            }

            return value
          },
        },
        props: {
          decorations: state => {
            const pluginState = lensesKey.getState(state)
            return pluginState?.decorations || DecorationSet.empty
          },
        },
      }),
    ]
  },
})
